/**
 * api.ts — Cliente de datos del visualizador Nexo (mismo principio portable que
 * el frontend AML, ADR 0001): fetch puro contra GoTrue + PostgREST, nunca
 * @supabase/supabase-js. Solo cuatro RPCs de lectura (migración 0073) y el
 * login por password grant. Sin tenant ni rol: Nexo es dato de referencia
 * global y cualquier usuario autenticado del proyecto puede leerlo.
 */
import type { NombreParecido } from "./parecidos.ts";
import { ApiError, errorDeAborto, errorDeRespuesta } from "./api-error.ts";
import type { SujetoBusqueda } from "./ficha/buscador.ts";
import { argumentoEvidencia, argumentosDetalle, argumentoSujeto, type LlaveEvidencia, type SujetoFicha } from "./ficha/argumentos.ts";
import type { GrafoSiger, LazoSiger } from "../../shared/piezas/red.ts";
export type { NombreParecido } from "./parecidos.ts";
export type { SujetoBusqueda } from "./ficha/buscador.ts";


const URL_BASE: string = import.meta.env.VITE_SUPABASE_URL ?? "";
const ANON_KEY: string = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";
const REQUEST_TIMEOUT_MS = 30_000;
const MS_POR_SEGUNDO = 1000;
/** Margen antes de la expiración del JWT para refrescar proactivamente. */
const MARGEN_REFRESH_MS = 60_000;
const LS_SESION = "nexo.sesion";

export function apiConfigurada(): boolean {
  return URL_BASE.length > 0 && !URL_BASE.includes("placeholder") && ANON_KEY.length > 0;
}

// ApiError y cómo se arma desde una respuesta o un aborto viven en api-error.ts (sin import.meta.env, probado con Deno).
export { ApiError } from "./api-error.ts";

// ─────────────────────────────────────────────────────────────────────────────
// HTTP base
// ─────────────────────────────────────────────────────────────────────────────

interface ReqOpts {
  method?: string;
  token?: string;
  body?: unknown;
  /** Cancelación desde la pantalla (etapa 4: la ficha cancela la lectura al irse). Se distingue del tiempo agotado. */
  signal?: AbortSignal | undefined;
}

async function req(path: string, opts: ReqOpts = {}): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  const externa = opts.signal;
  const cancelar = () => { controller.abort(); };
  if (externa?.aborted) controller.abort();
  externa?.addEventListener("abort", cancelar);
  try {
    const res = await fetch(`${URL_BASE}${path}`, {
      method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
      headers: {
        apikey: ANON_KEY,
        "Content-Type": "application/json",
        ...(opts.token ? { Authorization: `Bearer ${opts.token}` } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : null,
      signal: controller.signal,
    });
    if (!res.ok) {
      let cuerpo: unknown = null;
      try {
        cuerpo = await res.json();
      } catch {
        /* cuerpo no-JSON: conservar statusText */
      }
      throw errorDeRespuesta(cuerpo, res.statusText, res.status);
    }
    return res;
  } catch (e) {
    // Cancelada por la pantalla: se propaga como cancelación, no como «tardó demasiado».
    throw errorDeAborto(e, externa?.aborted === true);
  } finally {
    clearTimeout(timeoutId);
    externa?.removeEventListener("abort", cancelar);
  }
}

async function json<T>(res: Response): Promise<T> {
  const data: unknown = await res.json();
  return data as T;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sesión (GoTrue password grant; aal1 basta — solo lectura de dato global)
// ─────────────────────────────────────────────────────────────────────────────

export interface Sesion {
  accessToken: string;
  refreshToken: string | null;
  /** ms epoch de expiración del access token (del claim exp). */
  expiresAt: number | null;
  email: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  user?: { email?: string };
}

function jwtPayload(token: string): Record<string, unknown> {
  const parte = token.split(".")[1];
  if (!parte) return {};
  try {
    const data: unknown = JSON.parse(atob(parte.replace(/-/g, "+").replace(/_/g, "/")));
    return (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function sesionDesdeToken(tok: TokenResponse, emailFallback: string): Sesion {
  const claims = jwtPayload(tok.access_token);
  const exp = claims["exp"];
  return {
    accessToken: tok.access_token,
    refreshToken: tok.refresh_token ?? null,
    expiresAt: typeof exp === "number"
      ? exp * MS_POR_SEGUNDO
      : typeof tok.expires_in === "number"
        ? Date.now() + tok.expires_in * MS_POR_SEGUNDO
        : null,
    email: tok.user?.email ?? emailFallback,
  };
}

export function sesionGuardada(): Sesion | null {
  try {
    const crudo = localStorage.getItem(LS_SESION);
    if (!crudo) return null;
    const s: unknown = JSON.parse(crudo);
    if (!s || typeof s !== "object") return null;
    const o = s as Record<string, unknown>;
    if (typeof o["accessToken"] !== "string" || typeof o["email"] !== "string") return null;
    return s as Sesion;
  } catch {
    return null;
  }
}

export function guardarSesion(s: Sesion | null): void {
  try {
    if (s === null) localStorage.removeItem(LS_SESION);
    else localStorage.setItem(LS_SESION, JSON.stringify(s));
  } catch {
    /* localStorage bloqueado: la sesión vive solo en memoria */
  }
}

export async function iniciarSesion(email: string, password: string): Promise<Sesion> {
  const res = await req("/auth/v1/token?grant_type=password", { body: { email, password } });
  return sesionDesdeToken(await json<TokenResponse>(res), email);
}

export async function refrescarSesion(s: Sesion): Promise<Sesion> {
  if (!s.refreshToken) throw new ApiError("La sesión expiró y no hay refresh token.", 401);
  const res = await req("/auth/v1/token?grant_type=refresh_token", {
    body: { refresh_token: s.refreshToken },
  });
  return sesionDesdeToken(await json<TokenResponse>(res), s.email);
}

// ─────────────────────────────────────────────────────────────────────────────
// Cliente autenticado: refresca proactivamente y reintenta UNA vez ante 401.
// ─────────────────────────────────────────────────────────────────────────────

export interface ClienteNexo {
  readonly sesion: Sesion;
  rpc: <T>(fn: string, args: Record<string, unknown>, signal?: AbortSignal) => Promise<T>;
  edgeGet: (path: string) => Promise<Response>;
}

/**
 * Crea el cliente RPC ligado a una sesión. `onSesion` recibe la sesión
 * refrescada (para persistirla) y `onExpirada` se dispara cuando ni el
 * refresh salva la sesión (la UI regresa al login).
 */
export function crearCliente(
  sesionInicial: Sesion,
  onSesion: (s: Sesion) => void,
  onExpirada: () => void,
): ClienteNexo {
  let sesion = sesionInicial;
  let refrescando: Promise<Sesion> | null = null;

  async function refrescar(): Promise<Sesion> {
    refrescando ??= refrescarSesion(sesion)
      .then((nueva) => {
        sesion = nueva;
        onSesion(nueva);
        return nueva;
      })
      .finally(() => {
        refrescando = null;
      });
    return refrescando;
  }

  async function rpc<T>(fn: string, args: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
    if (sesion.expiresAt !== null && sesion.expiresAt - Date.now() < MARGEN_REFRESH_MS && sesion.refreshToken) {
      try {
        await refrescar();
      } catch {
        onExpirada();
        throw new ApiError("La sesión expiró. Vuelve a entrar.", 401);
      }
    }
    try {
      return await json<T>(await req(`/rest/v1/rpc/${fn}`, { token: sesion.accessToken, body: args, signal }));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401 && sesion.refreshToken) {
        try {
          await refrescar();
        } catch {
          onExpirada();
          throw new ApiError("La sesión expiró. Vuelve a entrar.", 401);
        }
        return json<T>(await req(`/rest/v1/rpc/${fn}`, { token: sesion.accessToken, body: args, signal }));
      }
      throw e;
    }
  }

  async function edgeGet(path: string): Promise<Response> {
    if (sesion.expiresAt !== null && sesion.expiresAt - Date.now() < MARGEN_REFRESH_MS && sesion.refreshToken) {
      try {
        await refrescar();
      } catch {
        onExpirada();
        throw new ApiError("La sesión expiró. Vuelve a entrar.", 401);
      }
    }

    async function llamar(): Promise<Response> {
      return await fetch(`${URL_BASE}${path}`, {
        method: "GET",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${sesion.accessToken}` },
      });
    }

    let res = await llamar();
    if (res.status === 401 && sesion.refreshToken) {
      try {
        await refrescar();
      } catch {
        onExpirada();
        throw new ApiError("La sesión expiró. Vuelve a entrar.", 401);
      }
      res = await llamar();
    }
    if (!res.ok) {
      let detalle = res.statusText;
      try {
        const cuerpo = await res.json() as Record<string, unknown>;
        if (typeof cuerpo["error"] === "string") detalle = cuerpo["error"];
      } catch {
        /* respuesta no JSON */
      }
      throw new ApiError(detalle, res.status);
    }
    return res;
  }

  return {
    get sesion() { return sesion; },
    rpc,
    edgeGet,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de las RPCs del visualizador (migración 0073)
// ─────────────────────────────────────────────────────────────────────────────

/** Severidad del catálogo nexo.flag_categoria (eje propio, §7.7). */
export type SeveridadFlag = "fuerte" | "media" | "debil" | "exculpatoria";

/** Resumen de flag fiscal por entidad (explorador). */
export interface FlagResumen {
  lista: string;
  categoria: string;
  severidad: SeveridadFlag;
}

/** Flag fiscal completo (ficha), bitemporal. */
export interface FlagFicha extends FlagResumen {
  rfc: string;
  supuesto: string | null;
  entidad_federativa: string | null;
  fecha_publicacion: string | null;
  valid_from: string;
  valid_to: string | null;
  vigente: boolean;
}

export interface StatsNexo {
  entidades: number;
  sanciones_activas: number;
  flags_activos: number;
  aristas: number;
  nombres: number;
  identificadores: number;
  por_schema: { schema: string; n: number }[];
  por_programa: { programa: string; autoridad: string; n: number }[];
  por_flag: { lista: string; categoria: string; severidad: SeveridadFlag; n: number }[];
  por_pais: { pais: string; n: number }[];
  aristas_matriz: { src: string; dst: string; n: number }[];
  /** Desglose por TIPO de relación (schema de la arista); tipada=rol del Advanced XML (0093). */
  por_arista_tipo?: { schema: string; tipada: boolean; n: number }[];
  fuentes: {
    id: string;
    nombre: string;
    autoridad: string;
    cadencia: string | null;
    ultima_carga: string | null;
    ultimo_estado: "corriendo" | "staged" | "ok" | "error" | null;
    ultima_iniciada: string | null;
    ultima_terminada: string | null;
    ultimo_detalle: string | null;
    pendientes: number;
    errores_24h: number;
    salud: "ok" | "alerta" | "critico";
  }[];
}

export interface FilaEntidad {
  id: string;
  schema: string;
  caption: string | null;
  pais: string | null;
  n_aristas: number;
  programas: string[];
  flags: FlagResumen[];
  fuentes: string[];
  listing_desde: string | null;
  /** Registro de comercio en la corrida vigente de Claude (nexo.siger_resumen_entidad); null si no hay identidad. */
  registro?: ResumenRegistro | null;
  /** @deprecated Llave de compatibilidad (2026-10-01): mismos datos que `registro`. Se retira con la fase F. */
  arbol_siger?: {
    expedientes: number;
    por_identificador: boolean;
    fecha_igual: boolean;
    nivel_max?: number;
    por_nivel?: { vinculada_ofac: number; confirmada: number; domicilio: number; probable: number };
  } | null;
}

export interface ResultadoEntidades {
  total: number;
  filas: FilaEntidad[];
}

export interface FiltrosExplorador {
  q?: string | null;
  schema?: string | null;
  pais?: string | null;
  programa?: string | null;
  fuente?: string | null;
  limit?: number;
  offset?: number;
  orden?: string;
  /** Registro de comercio: red (identidad confirmada o alta) · posible (media) · no · null = todas. */
  registro?: "red" | "posible" | "no" | null;
}

export interface FichaEntidad {
  entidad: {
    id: string;
    schema: string;
    caption: string | null;
    pais: string | null;
    props: Record<string, unknown>;
    first_seen: string;
    last_seen: string;
    vigente: boolean;
  };
  nombres: { nombre: string; tipo: string; low_quality: boolean }[];
  identificadores: { tipo: string; valor: string; pais: string | null; fraudulento: boolean }[];
  sanciones: {
    autoridad: string;
    programa: string;
    base_legal: string | null;
    listing_date: string | null;
    valid_from: string;
    valid_to: string | null;
    vigente: boolean;
    /** true = pseudo-sanción de compat (0075): es un flag fiscal, no eje red. */
    es_flag: boolean;
  }[];
  flags: FlagFicha[];
  xrefs: { fuente_id: string; native_id: string }[];
  aristas: {
    id: string;
    dir: "sale" | "entra";
    schema: string;
    rol: string | null;
    /** Calidad de la relación tipada del Advanced XML (0089): Unknown/High/Low. */
    calidad: string | null;
    porcentaje: number | null;
    contraparte_id: string;
    contraparte_caption: string | null;
    contraparte_schema: string;
    contraparte_sancionada: boolean;
  }[];
  /** Documentos FinCEN/OFAC que mencionan/designan a la entidad (0090). */
  documentos: DocumentoMencion[];
  /** Cruce de listas SAT ↔ OFAC/FinCEN donde participa (0091). */
  cruce: FilaCruce[];
}

export interface DocumentoMencion {
  id: string;
  autoridad: string;
  tipo: string;
  titulo: string;
  fecha: string;
  url: string | null;
  sar_key_term: string | null;
  rol: "designado" | "mencionado" | "sujeto_medida";
}

export interface DocumentoResumen {
  id: string;
  autoridad: string;
  tipo: string;
  /** Fuente del documento (0101): para categorizar/filtrar. Ausente contra backend < 0101. */
  fuente_id?: string;
  /** Códigos OFI del catálogo del corpus (0109); distinguen indictments homónimos. Ausente contra backend < 0109. */
  codigos?: string[];
  titulo: string;
  fecha: string;
  url: string | null;
  sar_key_term: string | null;
  esquema: string | null;
  resumen: string | null;
  red_flags: number;
  entidades: number;
  /** Conteos de extracción documental (0101); ausentes contra backend < 0101. */
  menciones?: number;
  afirmaciones?: number;
  relacionados: { documento: string; tipo: string }[];
}

export interface DocumentoDetalle {
  documento: {
    id: string;
    autoridad: string;
    tipo: string;
    titulo: string;
    fecha: string;
    url: string | null;
    sar_key_term: string | null;
    esquema: string | null;
    resumen: string | null;
    props: Record<string, unknown>;
  };
  red_flags: { numero: number | null; texto: string; tema: string | null }[];
  entidades: { entidad_id: string; rol: string; caption: string | null; schema: string; pais: string | null }[];
  relacionados: { documento: string; tipo: string; dir: "sale" | "entra"; titulo: string | null }[];
}

export interface FilaCruce {
  id?: string;
  rfc: string;
  nombre_sat: string | null;
  tipo_persona?: string | null;
  entidad_id: string;
  entidad_nombre: string | null;
  nombre_matcheado: string | null;
  metodo: "rfc" | "folio_mercantil" | "nombre_exacto" | "nombre_similar";
  score: number;
  confirmado: boolean;
  listas: { lista: string; categoria: string }[];
  autoridades: string[];
  refrescado_en?: string;
}

export interface ResultadoCruce {
  total: number;
  filas: FilaCruce[];
  ultimo_refresco: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Nexo Justicia (0094). Son propuestas y eventos atribuidos a documentos;
// nunca se presentan como sanciones ni como hechos aprobados por defecto.
// ─────────────────────────────────────────────────────────────────────────────

export type DecisionRevision = "approved" | "rejected";

export interface PropuestaJusticia {
  id: string;
  documento_id: string;
  titulo: string;
  fuente_id: string;
  predicate: string;
  subject_text: string | null;
  object_text: string | null;
  postura: string;
  evidence_excerpt: string;
  evidence_locator: Record<string, unknown> | null;
  created_at: string;
}

export interface ResultadoRevisionJusticia {
  id: string | number;
  estado: DecisionRevision;
  reviewed_by?: string;
  reviewed_at?: string;
}

export interface ResolucionJusticia {
  id: number;
  entity_id: string | null;
  method: string;
  score?: number | null;
  review_status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
}

export interface DocumentoJusticiaDetalle {
  documento: {
    id: string;
    source_native_id: string;
    fuente_id: string;
    authority: string;
    title: string;
    type: string;
    canonical_url: string | null;
    published_at: string | null;
  };
  version: {
    id: number | null;
    payload_sha256: string | null;
    retrieved_at: string | null;
    snapshot_path: string | null;
    parser_version: string | null;
  };
  assertions: Array<{
    id: string;
    predicate: string;
    subject_text: string | null;
    object_text: string | null;
    posture: string;
    review_status: string;
    reviewed_by: string | null;
    reviewed_at: string | null;
    evidence_excerpt: string;
    evidence_locator: Record<string, unknown> | null;
  }>;
  mentions: Array<{
    id: number;
    mention_text: string;
    mention_type: string;
    proposed_role: string | null;
    evidence_excerpt: string;
    evidence_locator: Record<string, unknown> | null;
    resolutions: ResolucionJusticia[];
  }>;
}

export interface EventoJusticiaEntidad {
  assertion_id: string;
  event_type: string | null;
  event_at: string | null;
  predicate: string;
  posture: string;
  review_status: "approved" | "proposed";
  source_id: string;
  source_native_id: string;
  source_url: string | null;
  document_title: string;
  payload_sha256: string | null;
  retrieved_at: string | null;
  evidence_excerpt: string;
  evidence_locator: Record<string, unknown> | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
}

export interface TimelineJusticiaEntidad {
  entity_id: string;
  approved: EventoJusticiaEntidad[];
  pending: EventoJusticiaEntidad[];
}

export interface NodoRed {
  id: string;
  dist: number;
  schema: string;
  caption: string | null;
  pais: string | null;
  sancionado: boolean;
  /** Eje propio (0078): severidad máxima de flags fiscales vigentes, o null. */
  flag_severidad: Exclude<SeveridadFlag, "exculpatoria"> | null;
}

export interface AristaRed {
  id: string;
  src: string;
  dst: string;
  schema: string;
  rol: string | null;
  porcentaje: number | null;
}

export interface RedNexo {
  centro: string;
  saltos: number;
  nodos: NodoRed[];
  aristas: AristaRed[];
  truncado: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Extracción documental (0097): grafo por documento y capa documental de la
// ficha canónica. Todo lo que llega aquí es 'proposed' salvo revisión previa.
// ─────────────────────────────────────────────────────────────────────────────

export interface NodoRedDocumento {
  norm: string;
  texto: string;
  n_afirmaciones: number;
  /** Tipo de mención (person/company/organization/location/unknown) o null si el nombre solo aparece en afirmaciones. */
  tipo: string | null;
  /** Id de la resolución elegida (para revisión en contexto); null sin candidato o contra backend < 0100. */
  resolucion_id: number | null;
  entidad_id: string | null;
  entidad_caption: string | null;
  entidad_schema: string | null;
  resolucion_estado: "proposed" | "approved" | null;
  resolucion_score: number | null;
}

export interface AristaRedDocumento {
  id: string;
  src: string;
  dst: string;
  predicate: string;
  estado: "proposed" | "approved";
  postura: string;
}

export interface AfirmacionDocumento {
  id: string;
  subject_text: string;
  predicate: string;
  object_text: string;
  estado: "proposed" | "approved";
  postura: string;
  /** Estado probatorio fino del extractor (mentioned/reported/alleged/investigated/denied). */
  claim_status: string | null;
  page: number | null;
  confidence: number | null;
  evidence_excerpt: string;
}

export interface RedDocumento {
  documento: string;
  nodos: NodoRedDocumento[];
  aristas: AristaRedDocumento[];
  /** Detalle tabular (0099); ausente contra un backend con solo 0097. */
  afirmaciones?: AfirmacionDocumento[] | undefined;
}

export interface MencionResuelta {
  resolucion_id: number;
  estado: "proposed" | "approved";
  metodo: string;
  score: number | null;
  mencion: string;
  tipo: string;
  documento_id: string;
  titulo: string | null;
}

export interface AfirmacionExtraida {
  id: string;
  documento_id: string;
  titulo: string | null;
  fuente_id: string;
  url: string | null;
  predicate: string;
  subject_text: string | null;
  object_text: string | null;
  postura: string;
  review_status: "proposed" | "approved";
  confidence: number | null;
  evidence_excerpt: string;
  evidence_locator: Record<string, unknown> | null;
}

export interface ExtraccionEntidad {
  menciones: MencionResuelta[];
  afirmaciones: AfirmacionExtraida[];
}

export async function vizDocumentoRed(cliente: ClienteNexo, id: string): Promise<RedDocumento | null> {
  return cliente.rpc<RedDocumento | null>("nexo_viz_documento_red", { p_id: id });
}

export async function vizEntidadExtraccion(cliente: ClienteNexo, id: string): Promise<ExtraccionEntidad> {
  return cliente.rpc<ExtraccionEntidad>("nexo_viz_entidad_extraccion", { p_entidad: id });
}

/**
 * Registro de comercio de una entidad OFAC en la corrida vigente de Claude (migración 20261001170000).
 * La frase de cada liga la arma la base (nexo.siger_describir_vinculo): el front sólo la muestra.
 */
export interface ResumenRegistro {
  runId: string;
  /** red: identidad confirmada o alta · posible: identidad media, a confirmar en sucursal · co: sin identidad, pero
   * OFAC registra su dirección «c/o» una empresa del registro (20261001220000). */
  estado: "red" | "posible" | "co";
  nivel: "confirmada" | "alta" | "media" | null;
  /** 20261005120000: ¿alguna identidad de la entidad en el registro está corroborada? Con `red` y `false`, los textos dicen «Posible…». */
  corroborada?: boolean;
  /** Texto del distintivo, armado por la base (un solo dueño para Nexo y Argus Team; desde 20261001220000). */
  distintivo: string;
  /** Ligas c/o (OFAC registra la dirección «al cuidado de» esta empresa del registro). */
  co?: number;
  senales: string[];
  identidadTexto: string;
  recomendacion: string | null;
  expedientes: number;
  empresas: number;
  personas: number;
  /** Otros sancionados ligados (no cuentan en empresas ni personas). */
  sancionados?: number;
  /** Registros que sólo coinciden a nivel medio con una entidad ya identificada: no se cuentan en la red. */
  coincidenciasParciales?: number;
  ligas: number;
  excluidas: number;
  frase: string | null;
}
export type GrupoLiga = "directa" | "en_comun" | "indirecta" | "autoridad_litigio";
export interface LigaRegistro {
  actorId: string;
  nombre: string;
  tipo: string;
  frase: string;
  grupo: GrupoLiga;
  pasos: number;
  /** El tipo de relación (nexo.siger_tipo_liga, 2026-10-02), o "co": OFAC registra la dirección del sancionado al cuidado de
   * esta empresa (sin camino en el registro). */
  tipoLiga?: string;
  tipoTexto?: string | null;
  tipoExplicacion?: string | null;
  actorEsOfac: boolean;
  vigente: boolean | null;
  fuerza: number | null;
  /** Sólo con detalle completo: el camino con su evidencia y citas. */
  camino?: LazoSiger[];
}
/** 2026-10-02: los grupos son los tipos de relación (clave = tipo, o «co»), en orden de peso. */
export interface GrupoRegistro {
  clave: string;
  titulo: string;
  explicacion?: string | null;
  orden?: number;
  ligas: LigaRegistro[];
}
/** Lo que declara OFAC de una entidad (nexo.ofac_relaciones_entidad; frases con la regla de #167). */
export interface SegunOfac {
  fuente: string;
  corte: string | null;
  total: number;
  mostradas: number;
  relaciones: { contraparteId: string; contraparte: string; contraparteTipo: string; programas: string[]; rol: string; saliente: boolean; frase: string }[];
}
export interface RedRegistro {
  aviso: string;
  segunOfac?: SegunOfac | null;
  run: { id: string; extractor?: string; reglas?: string; corteOfac: string | null; creado?: string } | null;
  resumen: ResumenRegistro | null;
  grupos: GrupoRegistro[];
  totales: { ligas: number; mostradas: number; omitidas: number; motivoOmision?: string | null; excluidas: number; motivoExclusion?: string | null; expedientes?: number; expedientesMostrados?: number };
}

export async function vizEntidadRedRegistro(cliente: ClienteNexo, id: string): Promise<RedRegistro> {
  return cliente.rpc<RedRegistro>("nexo_viz_entidad_red_registro", { p_entidad: id, p_detalle: "completo" });
}

/**
 * Por qué la sancionó OFAC (public.nexo_viz_entidad_por_que, migración 20261002020000; dueña: sesión treasury). Los comunicados
 * del Departamento de Estado entran por la misma fuente; su documento_id empieza con «us_treasury_ofac:state:».
 */
export interface PorQueOfac {
  entidad_id: string;
  fecha_alta_en_lista: string | null;
  programas: { programa: string; autoridad: string; tema: string | null; base_legal: string | null; explicacion: string | null; estado_medida: "regla_final" | "propuesta" | null }[];
  comunicados_directos: { documento_id: string; titulo: string; fecha: string | null; url: string | null; rol: string; clase: string | null; metodo: string;
    fragmento: { fragment_id: string | null; start: number | null; end: number | null; texto: string | null; parrafo: string | null } }[];
  recent_actions: { documento_id: string; titulo: string; fecha: string | null; url: string | null }[];
  comunicados_de_la_accion: { documento_id: string; titulo: string; fecha: string | null; url: string | null; via_recent_action: string }[];
  organigramas: { documento_id: string; version_id: number; titulo: string; fecha: string | null; url: string | null; tiene_archivo: boolean; procedencia: string; wayback_url: string | null; wayback_timestamp: string | null }[];
  fotos: { documento_id: string; version_id: number; titulo: string; fecha: string | null; url: string | null; tiene_archivo: boolean; pie: string | null; alt: string | null; credito: string | null; procedencia: string }[];
  menciones: { total: number; por_fuente: Record<string, number>; nota: string;
    ejemplos: { documento_id: string; titulo: string; fuente: string; fecha: string | null; url: string | null; texto: string; extracto: string | null; estado: "propuesta" | "confirmada"; otros_candidatos: number }[] };
  afirmaciones: unknown[];
  totales: { comunicados_directos: number; recent_actions: number; comunicados_de_la_accion: number; organigramas: number; fotos: number; fotos_de_la_accion: number };
}

export async function vizEntidadPorQue(cliente: ClienteNexo, id: string): Promise<PorQueOfac | null> {
  return cliente.rpc<PorQueOfac | null>("nexo_viz_entidad_por_que", { p_entidad: id });
}

/**
 * Grafo SIGER extraído por Claude y consolidado por reglas (migración nexo_siger_grafo_claude). Etapa 4, ajustes 4d: su forma vive en
 * app/shared/piezas/red.ts (la misma que lee Argus Team por nexo.red); desde 20261005120000 cada identidad trae `corroborada` y cada
 * relación `ancla_corroborada`.
 */
export type {
  ActorGrafoSiger, AnclaSiger, EvidenciaLazo, FlagSatSiger, GrafoSiger, LazoSiger, OfacDeActor, RelacionOfacSiger, ValorIdentidadSiger, VinculoSiger,
} from "../../shared/piezas/red.ts";

export async function vizEntidadGrafoSiger(cliente: ClienteNexo, id: string): Promise<GrafoSiger | null> {
  return cliente.rpc<GrafoSiger | null>("nexo_viz_entidad_grafo_siger", { p_entidad: id });
}

export interface DatasetExportacion {
  id: string;
  archivo: string;
  titulo: string;
  descripcion: string;
  ultima_carga: string | null;
}

export interface CatalogoExportaciones {
  generado_en: string;
  datasets: DatasetExportacion[];
  categorias_69: Omit<DatasetExportacion, "ultima_carga">[];
}

export async function vizStats(cliente: ClienteNexo): Promise<StatsNexo> {
  return cliente.rpc<StatsNexo>("nexo_viz_stats", {});
}

/** Personas y empresas de la estructura SIGER que coinciden con la búsqueda (no son entidades de la lista). */

/** Personas y empresas del registro de comercio con relación con OFAC: la misma regla que el buscador de Argus Team. */
export async function vizBuscarSujetosRegistro(cliente: ClienteNexo, q: string): Promise<{ total: number; filas: SujetoBusqueda[] }> {
  // Corte 4a-2 de los certificados del SAT: también los representantes sin ficha confirmada (filas sat_rep), que esta pantalla sabe pintar.
  // Revisión independiente: una base sin 20261017233000 no conoce satRep (400); el recuadro sigue, sin representantes.
  try {
    return await cliente.rpc<{ total: number; filas: SujetoBusqueda[] }>("nexo_viz_buscar_sujetos", { p: { query: q, kind: "siger_actor", satRep: true } });
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 400) throw e;
    return cliente.rpc<{ total: number; filas: SujetoBusqueda[] }>("nexo_viz_buscar_sujetos", { p: { query: q, kind: "siger_actor" } });
  }
}

/**
 * Nombres parecidos cuando la búsqueda por nombre no encontró a nadie (2026-10-02; nexo.buscar_parecidos). Una base sin la función
 * (versión anterior) o cualquier error responde «ninguno»: la pantalla sigue diciendo que no hay resultados, como antes.
 */
export async function vizBuscarParecidos(cliente: ClienteNexo, q: string): Promise<NombreParecido[]> {
  try {
    const r = await cliente.rpc<{ filas?: NombreParecido[] } | null>("nexo_viz_buscar_parecidos", { p: { query: q } });
    return Array.isArray(r?.filas) ? r.filas.slice(0, 5) : [];
  } catch {
    return [];
  }
}

export async function vizEntidades(cliente: ClienteNexo, f: FiltrosExplorador): Promise<ResultadoEntidades> {
  return cliente.rpc<ResultadoEntidades>("nexo_viz_entidades", {
    p_q: f.q ?? null,
    p_schema: f.schema ?? null,
    p_pais: f.pais ?? null,
    p_programa: f.programa ?? null,
    p_fuente: f.fuente ?? null,
    p_limit: f.limit ?? 50,
    p_offset: f.offset ?? 0,
    p_orden: f.orden ?? "nombre",
    // Sólo se manda con filtro: sin él la llamada es la misma que antes de 20261001170000.
    ...(f.registro ? { p_registro: f.registro } : {}),
  });
}

export async function vizEntidad(cliente: ClienteNexo, id: string): Promise<FichaEntidad | null> {
  return cliente.rpc<FichaEntidad | null>("nexo_viz_entidad", { p_id: id });
}

export async function vizRed(cliente: ClienteNexo, id: string, saltos: number): Promise<RedNexo | null> {
  return cliente.rpc<RedNexo | null>("nexo_viz_red", { p_id: id, p_saltos: saltos });
}

export async function vizDocumentos(cliente: ClienteNexo): Promise<DocumentoResumen[]> {
  return cliente.rpc<DocumentoResumen[]>("nexo_viz_documentos", {});
}

export async function vizDocumento(cliente: ClienteNexo, id: string): Promise<DocumentoDetalle | null> {
  return cliente.rpc<DocumentoDetalle | null>("nexo_viz_documento", { p_id: id });
}

export interface FiltrosCruce {
  confirmado?: boolean | null;
  metodo?: string | null;
  q?: string | null;
  limit?: number;
  offset?: number;
}

export async function vizCruce(cliente: ClienteNexo, f: FiltrosCruce): Promise<ResultadoCruce> {
  return cliente.rpc<ResultadoCruce>("nexo_viz_cruce", {
    p_confirmado: f.confirmado ?? null,
    p_metodo: f.metodo ?? null,
    p_q: f.q ?? null,
    p_limit: f.limit ?? 50,
    p_offset: f.offset ?? 0,
  });
}

export async function justiciaListarPropuestas(
  cliente: ClienteNexo,
  limite = 50,
  despuesDe: string | null = null,
  despuesId: string | null = null,
): Promise<PropuestaJusticia[]> {
  return cliente.rpc<PropuestaJusticia[]>("nexo_justicia_listar_propuestas", {
    p_limite: limite,
    p_despues_de: despuesDe,
    p_despues_id: despuesId,
  });
}

export async function justiciaRevisarAfirmacion(
  cliente: ClienteNexo,
  id: string,
  decision: DecisionRevision,
  notas?: string | null,
): Promise<ResultadoRevisionJusticia> {
  return cliente.rpc<ResultadoRevisionJusticia>("nexo_justicia_revisar_afirmacion", {
    p_id: id,
    p_decision: decision,
    p_notas: notas?.trim() || null,
  });
}

export async function justiciaRevisarResolucion(
  cliente: ClienteNexo,
  id: number,
  decision: DecisionRevision,
  notas?: string | null,
): Promise<ResultadoRevisionJusticia> {
  return cliente.rpc<ResultadoRevisionJusticia>("nexo_justicia_revisar_resolucion", {
    p_id: id,
    p_decision: decision,
    p_notas: notas?.trim() || null,
  });
}

export async function justiciaDocumento(
  cliente: ClienteNexo,
  id: string,
): Promise<DocumentoJusticiaDetalle | null> {
  return cliente.rpc<DocumentoJusticiaDetalle | null>("nexo_justicia_documento", { p_id: id });
}

export async function justiciaEntidad(
  cliente: ClienteNexo,
  entidadId: string,
): Promise<TimelineJusticiaEntidad | null> {
  return cliente.rpc<TimelineJusticiaEntidad | null>("nexo_justicia_entidad", {
    p_entidad: entidadId,
  });
}

export async function catalogoExportaciones(cliente: ClienteNexo): Promise<CatalogoExportaciones> {
  return await json<CatalogoExportaciones>(
    await cliente.edgeGet("/functions/v1/nexo-export?dataset=catalogo"),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Visor de revisión documental (20260714000105). Fragmentos con offsets +
// menciones/afirmaciones tal como las capturó el extractor, más las RPCs de
// revisión/creación manual. Espeja la forma de 1.5 del diseño; los campos que
// no se usan en la UI actual igual se tipan porque la RPC los devuelve.
// ─────────────────────────────────────────────────────────────────────────────

export interface FragmentoVisor {
  fragment_id: string;
  seq: number;
  page: number | null;
  texto: string;
}

export type DecisionMencion = "confirmed" | "rejected";
export type EstadoResolucionVisor = "proposed" | "approved" | "rejected";
export type EstadoAfirmacionVisor = "proposed" | "approved" | "rejected";

export interface ResolucionVisor {
  id: number;
  entidad_id: string;
  caption: string | null;
  schema: string;
  metodo: string;
  score: number | null;
  estado: EstadoResolucionVisor;
  reviewed_at: string | null;
}

export interface RevisionMencionVisor {
  decision: DecisionMencion;
  reviewed_at: string;
  notas: string | null;
}

export interface MencionVisor {
  id: number;
  mention_text: string;
  mention_type: string;
  proposed_role: string | null;
  confidence: number | null;
  extractor_version: string | null;
  evidence_locator: Record<string, unknown> | null;
  revision: RevisionMencionVisor | null;
  resoluciones: ResolucionVisor[];
}

export interface AfirmacionVisor {
  id: string;
  subject_text: string | null;
  predicate: string;
  object_text: string | null;
  postura: string;
  estado: EstadoAfirmacionVisor;
  confidence: number | null;
  extraction_method: string | null;
  extractor_version: string | null;
  evidence_excerpt: string;
  evidence_locator: Record<string, unknown> | null;
  reviewed_by_present: boolean;
  reviewed_at: string | null;
  review_notes: string | null;
}

/** Ubicación del PDF original en Storage (Fase 3a, 0107) — nunca se usa
 * directo: siempre se cambia por una URL firmada vía `visorSnapshotUrl`. */
export interface SnapshotVisor {
  bucket: string;
  path: string;
}

export interface VisorDocumento {
  documento: {
    id: string;
    titulo: string;
    autoridad: string;
    tipo: string;
    fecha: string;
    url: string | null;
    fuente_id: string;
  };
  version: {
    id: number;
    parser_version: string | null;
    retrieved_at: string | null;
    /** null si el documento no tiene PDF original subido (0107). */
    snapshot: SnapshotVisor | null;
  };
  fragmentos: FragmentoVisor[];
  menciones: MencionVisor[];
  afirmaciones: AfirmacionVisor[];
  predicados: string[];
}

export async function visorDocumento(cliente: ClienteNexo, id: string): Promise<VisorDocumento | null> {
  return cliente.rpc<VisorDocumento | null>("nexo_visor_documento", { p_id: id });
}

/** Pide a la Edge Function nexo-snapshot una URL firmada (600s) del PDF
 * original de `id`. Lanza ApiError si el documento no tiene snapshot. */
export async function visorSnapshotUrl(cliente: ClienteNexo, id: string): Promise<string> {
  const res = await cliente.edgeGet(`/functions/v1/nexo-snapshot?documento=${encodeURIComponent(id)}`);
  const { url } = await json<{ url: string }>(res);
  return url;
}

export async function visorRevisarMencion(
  cliente: ClienteNexo,
  id: number,
  decision: DecisionMencion,
  notas?: string | null,
): Promise<RevisionMencionVisor> {
  return cliente.rpc<RevisionMencionVisor>("nexo_visor_revisar_mencion", {
    p_id: id,
    p_decision: decision,
    p_notas: notas?.trim() || null,
  });
}

export interface ArgsCrearMencion {
  documento: string;
  fragmentId: string;
  start: number;
  len: number;
  tipo: string;
  rol?: string | null;
}

/** La mención regresa completa (con revision y resoluciones), lista para insertarse en el estado. */
export interface ResultadoCrearMencion {
  mencion: MencionVisor;
  duplicada?: boolean;
}

export async function visorCrearMencion(
  cliente: ClienteNexo,
  a: ArgsCrearMencion,
): Promise<ResultadoCrearMencion> {
  return cliente.rpc<ResultadoCrearMencion>("nexo_visor_crear_mencion", {
    p_documento: a.documento,
    p_fragment_id: a.fragmentId,
    p_start: a.start,
    p_len: a.len,
    p_tipo: a.tipo,
    p_rol: a.rol?.trim() || null,
  });
}

export interface ArgsCrearAfirmacion {
  documento: string;
  fragmentId: string;
  start: number;
  len: number;
  subject: string;
  predicate: string;
  object: string;
  claimStatus?: string;
  notas?: string | null;
}

export interface ResultadoCrearAfirmacion extends AfirmacionVisor {
  duplicada?: boolean;
}

export async function visorCrearAfirmacion(
  cliente: ClienteNexo,
  a: ArgsCrearAfirmacion,
): Promise<ResultadoCrearAfirmacion> {
  return cliente.rpc<ResultadoCrearAfirmacion>("nexo_visor_crear_afirmacion", {
    p_documento: a.documento,
    p_fragment_id: a.fragmentId,
    p_start: a.start,
    p_len: a.len,
    p_subject: a.subject,
    p_predicate: a.predicate,
    p_object: a.object,
    p_claim_status: a.claimStatus ?? "mentioned",
    p_notas: a.notas?.trim() || null,
  });
}

export async function visorVincularMencion(
  cliente: ClienteNexo,
  mencionId: number,
  entidadId: string,
  notas?: string | null,
): Promise<ResolucionVisor> {
  return cliente.rpc<ResolucionVisor>("nexo_visor_vincular_mencion", {
    p_mencion_id: mencionId,
    p_entidad: entidadId,
    p_notas: notas?.trim() || null,
  });
}

/**
 * Descarga a disco en streaming cuando el navegador ofrece File System Access
 * (Chrome/Edge). El fallback universal usa Blob para mantener compatibilidad.
 */
export async function descargarExportacion(
  cliente: ClienteNexo,
  dataset: DatasetExportacion,
  informarBytes?: (bytes: number) => void,
): Promise<void> {
  // Una petición por lote: evita depender de una sola conexión Edge larga.
  // El archivo sólo se crea cuando un lote declara terminado=true.
  const partes: ArrayBuffer[] = [];
  let bytes = 0;
  let cursor: string | null = null;
  let terminado = false;
  let lotes = 0;
  while (!terminado) {
    const queryCursor = cursor ? `&cursor=${encodeURIComponent(cursor)}` : "";
    const res = await cliente.edgeGet(
      `/functions/v1/nexo-export?dataset=${encodeURIComponent(dataset.id)}&modo=chunk${queryCursor}`,
    );
    const parte = await res.arrayBuffer();
    partes.push(parte);
    bytes += parte.byteLength;
    informarBytes?.(bytes);
    terminado = res.headers.get("X-Nexo-Terminado") === "true";
    const siguiente = res.headers.get("X-Nexo-Next-Cursor");
    if (!terminado && !siguiente) {
      throw new ApiError("La exportación se interrumpió antes del último lote.");
    }
    cursor = siguiente;
    lotes += 1;
    if (lotes > 1_000) throw new ApiError("La exportación excedió el límite seguro de lotes.");
  }
  const blob = new Blob(partes, { type: "text/csv;charset=utf-8" });

  const url = URL.createObjectURL(blob);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = dataset.archivo;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  // Mantener el Blob disponible mientras el navegador registra la descarga;
  // revocarlo en el mismo tick puede cancelar archivos grandes en algunos
  // navegadores aunque el click ya haya ocurrido.
  window.setTimeout(() => { URL.revokeObjectURL(url); }, 60_000);
}

// ─────────────────────────────────────────────────────────────────────────────
// Etapa 4: lo que Argus afirma sobre una persona o empresa (nexo.hallazgos), por los envoltorios para Nexo
// (public.nexo_viz_hallazgos, nexo_viz_hallazgo_detalle y nexo_viz_evidencia; migración 20261003160000, con los lotes 0a de
// 20261004180000 y 20261004183000). El documento se lee con leerHallazgos (app/shared/hallazgos/leer.ts) antes de pintarlo.
// ─────────────────────────────────────────────────────────────────────────────

export type { LlaveEvidencia, SujetoFicha } from "./ficha/argumentos.ts";

/** El documento argus.hallazgos/1 tal como lo escribió la base (sin tipar: lo valida leerHallazgos). */
export async function vizHallazgos(cliente: ClienteNexo, s: SujetoFicha, signal?: AbortSignal): Promise<unknown> {
  return cliente.rpc<unknown>("nexo_viz_hallazgos", { p: argumentoSujeto(s) }, signal);
}

/** Un acto de nexo.hallazgo_detalle (corte 4a: la lista de actos con su fuente). */
export interface ActoDetalle {
  acto_id?: number;
  papel_texto: string | null;
  fecha: string | null;
  acciones: number | null;
  total_acciones: number | null;
  fuente: unknown;
}

/** Detalle de una contraparte (su referencia del documento, `entity:<uuid>`): los actos entre el sujeto y ella (argus.hallazgo_detalle/1). */
export async function vizHallazgoDetalle(cliente: ClienteNexo, s: SujetoFicha, contraparte: string, signal?: AbortSignal): Promise<{ actos: ActoDetalle[] }> {
  const args = argumentosDetalle(s, contraparte);
  if (!args) return { actos: [] };
  const r = await cliente.rpc<{ actos?: unknown } | null>("nexo_viz_hallazgo_detalle", args, signal);
  return { actos: Array.isArray(r?.actos) ? (r.actos as ActoDetalle[]) : [] };
}

/**
 * Corte 2 de los certificados del SAT: un certificado de nexo.certificados_detalle (argus.certificados/1), por
 * public.nexo_viz_certificados_detalle (con la lista de acceso de Nexo). El número de serie y la sucursal sólo se ven aquí (diseño §8).
 */
export interface CertificadoSat {
  papel: "titular" | "representante";
  nivel: "confirmada" | "posible";
  empresa: string | null;
  empresa_rfc: string | null;
  rep_curp: string | null;
  rep_rfc: string | null;
  correo: string | null;
  numero_serie: string | null;
  sucursal: string | null;
  emitido: string | null;
  vence: string | null;
}

/** Todos los certificados del SAT de un registro («Ver todos los certificados»): hasta 200 y cuántos son. */
export async function vizCertificados(cliente: ClienteNexo, s: SujetoFicha, signal?: AbortSignal): Promise<{ total: number; certificados: CertificadoSat[] }> {
  const r = await cliente.rpc<{ total?: unknown; certificados?: unknown } | null>("nexo_viz_certificados_detalle", { p: argumentoSujeto(s) }, signal);
  const certificados = Array.isArray(r?.certificados) ? (r.certificados as CertificadoSat[]) : [];
  return { total: typeof r?.total === "number" ? r.total : certificados.length, certificados };
}

/** nexo.evidencia: un acto del Registro Público de Comercio o un párrafo de un comunicado. */
export type Evidencia =
  | { tipo: "acta"; corrida: string; acto: {
      acto_id: number; papel_texto?: string | null; fecha?: string | null; fecha_instrumento?: string | null; fecha_inscripcion?: string | null;
      persona?: { nombre: string } | null; sociedad?: { nombre: string } | null; contraparte?: { nombre: string } | null;
      acciones?: number | null; total_acciones?: number | null; porcentaje_impreso?: number | null; archivo?: string | null; pagina?: number | null; cita?: string | null;
      verificacion?: { texto_presente: boolean | null; ubicacion: boolean | null; atribucion: boolean | null } | null } }
  | { tipo: "comunicado"; version_id: number; version_vigente: boolean;
      documento: { titulo?: string | null; fecha?: string | null; url?: string | null };
      fragmento: { fragment_id: string; pagina?: number | null; texto: string };
      traduccion: { voz?: string; texto_es: string; estado?: string } | null };

export async function vizEvidencia(cliente: ClienteNexo, llave: LlaveEvidencia, signal?: AbortSignal): Promise<Evidencia> {
  return cliente.rpc<Evidencia>("nexo_viz_evidencia", { p: argumentoEvidencia(llave) }, signal);
}
